/**
 * Server quote is the only verified Hubwayz price.
 * A local calculation is not shown as verified when the quote is missing.
 */

const ALLOWED_QUOTE_FIELDS = [
  "offerCode",
  "searchId",
  "hotelId",
  "checkIn",
  "checkOut",
  "rooms",
  "roomName",
  "board",
  "currency",
  "displayCurrency",
  "roomCount",
  "offerToken",
  "acknowledgePriceChange",
];

function finiteNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

export function buildHubwayzQuoteRequest(input = {}) {
  const body = {};
  for (const key of ALLOWED_QUOTE_FIELDS) {
    if (input[key] !== undefined && input[key] !== null && input[key] !== "") {
      body[key] = input[key];
    }
  }
  if (body.acknowledgePriceChange !== true) delete body.acknowledgePriceChange;
  return body;
}

/**
 * Commission change for one stored offer.
 * Identity plus commission type and value only. A payable amount is not sent.
 */
export function buildHubwayzCustomerPriceRequest(input = {}) {
  const body = buildHubwayzQuoteRequest(input);
  const raw = String(input.commissionType || "").trim().toLowerCase();
  const mapped =
    raw === "%" || raw === "percentage"
      ? "percentage"
      : raw === "+" || raw === "fixed_ils"
        ? "fixed_ils"
        : null;
  const value = Number(input.commissionValue);
  if (!mapped || !Number.isFinite(value) || value < 0) return null;
  if (mapped === "percentage" && value > 100) return null;
  if (mapped === "fixed_ils" && value > 1_000_000) return null;
  body.commissionType = mapped;
  body.commissionValue = Math.round(value * 100) / 100;
  return body;
}

/**
 * Immediate commission display. Same rule as the server: a percentage is
 * ceiled once on the verified agent net, a fixed amount is added once in ILS.
 * The charge stays the net. The server response replaces this preview.
 */
export function previewHubwayzCommissionQuote(quote, commissionType, commissionValue) {
  const net = finiteNumber(quote?.agentNetIls);
  if (quote?.binding !== true || net == null || net <= 0) return null;
  const raw = String(commissionType || "").trim().toLowerCase();
  const type =
    raw === "%" || raw === "percentage"
      ? "percentage"
      : raw === "+" || raw === "fixed_ils"
        ? "fixed_ils"
        : null;
  const value = finiteNumber(commissionValue);
  if (!type || value == null || value < 0) return null;
  if (type === "percentage" && value > 100) return null;
  const markup =
    value === 0
      ? 0
      : type === "fixed_ils"
        ? value
        : Math.ceil(net * (value / 100) - 1e-9);
  const sale = net + markup;
  const netAmount = finiteNumber(quote.agentNetAmount ?? quote.dealAmount);
  const currency = String(
    quote.agentNetCurrency || quote.dealCurrency || quote.customerSaleCurrency || ""
  )
    .trim()
    .toUpperCase();
  const rate = netAmount != null && netAmount > 0 ? net / netAmount : null;
  const markupAmount = rate ? markup / rate : null;
  const saleAmount = rate && netAmount != null ? netAmount + markupAmount : null;
  return {
    ...quote,
    source: "server_quote",
    ok: true,
    binding: true,
    status: "quoted",
    commissionLocked: true,
    commissionPreview: true,
    agentNetIls: net,
    chargeIls: net,
    agentMarkupIls: markup,
    customerSaleIls: sale,
    customerPayIls: sale,
    agentCommissionType: type,
    agentCommissionValue: value,
    agentNetAmount: netAmount,
    agentNetCurrency: currency || quote.agentNetCurrency || null,
    agentMarkupAmount: markupAmount,
    agentMarkupCurrency: markupAmount != null ? currency || null : quote.agentMarkupCurrency,
    customerSaleAmount: saleAmount,
    customerSaleCurrency: saleAmount != null ? currency || null : quote.customerSaleCurrency,
  };
}

/** The server quote includes the commission the agent just chose. */
export function hubwayzCommissionMatchesRequest(view, commissionType, commissionValue) {
  const net = finiteNumber(view?.agentNetIls);
  if (view?.binding !== true || net == null || net <= 0) return false;
  const expected = previewHubwayzCommissionQuote(
    {
      binding: true,
      agentNetIls: net,
      chargeIls: finiteNumber(view.chargeIls) ?? net,
      agentNetAmount: finiteNumber(view.agentNetAmount ?? view.dealAmount),
      agentNetCurrency: view.agentNetCurrency || view.dealCurrency || view.customerSaleCurrency,
    },
    commissionType,
    commissionValue
  );
  if (!expected) return false;
  const markup = finiteNumber(view.agentMarkupIls) ?? 0;
  const sale = finiteNumber(view.customerSaleIls);
  return (
    Math.abs(markup - expected.agentMarkupIls) < 1 &&
    sale != null &&
    Math.abs(sale - expected.customerSaleIls) < 1
  );
}

export function serverQuotePriceView(data) {
  const row = data && typeof data === "object" ? data : {};
  const agentNetIls = finiteNumber(row.agentNetIls);
  const chargeIls = finiteNumber(row.chargeIls);
  const agentMarkupIls = finiteNumber(row.agentMarkupIls);
  const customerSaleIls = finiteNumber(row.customerSaleIls);
  const agentPriceAmount = finiteNumber(row.agentPriceAmount);
  const agentPriceCurrency = String(row.agentPriceCurrency || "").trim().toUpperCase() || null;
  const status = row.status || "quote_unavailable";
  const amountsMatch =
    agentNetIls != null &&
    chargeIls != null &&
    Math.abs(chargeIls - agentNetIls) < 0.001;
  const binding = row.binding === true && status === "quoted" && amountsMatch;
  const showAmounts = amountsMatch && (binding || status === "price_changed");
  const explicitPay = finiteNumber(row.customerPayIls);
  const pay = explicitPay != null ? explicitPay : customerSaleIls;
  const saleMatches =
    agentNetIls != null &&
    pay != null &&
    Math.abs(pay - (agentNetIls + (agentMarkupIls || 0))) < 0.001;
  const commissionType =
    row.agentCommissionType === "percentage" || row.agentCommissionType === "fixed_ils"
      ? row.agentCommissionType
      : null;
  return {
    source: "server_quote",
    ok: binding,
    binding,
    status,
    requiresAcknowledgement: row.requiresAcknowledgement === true || status === "price_changed",
    agentPriceAmount: showAmounts && agentPriceAmount > 0 ? agentPriceAmount : null,
    agentPriceCurrency: showAmounts ? agentPriceCurrency : null,
    agentNetIls: showAmounts ? agentNetIls : null,
    agentMarkupIls: showAmounts ? agentMarkupIls || 0 : 0,
    customerSaleIls: showAmounts ? customerSaleIls : null,
    customerPayIls: showAmounts && saleMatches ? pay : null,
    chargeIls: showAmounts ? chargeIls : null,
    dealAmount: showAmounts && finiteNumber(row.dealAmount) > 0 ? finiteNumber(row.dealAmount) : null,
    dealCurrency: showAmounts ? String(row.dealCurrency || "").trim().toUpperCase() || null : null,
    agentNetAmount: showAmounts ? finiteNumber(row.agentNetAmount) : null,
    agentNetCurrency: showAmounts ? String(row.agentNetCurrency || "").trim().toUpperCase() || null : null,
    agentMarkupAmount: showAmounts ? finiteNumber(row.agentMarkupAmount) : null,
    agentMarkupCurrency: showAmounts ? String(row.agentMarkupCurrency || "").trim().toUpperCase() || null : null,
    customerSaleAmount: showAmounts ? finiteNumber(row.customerSaleAmount) : null,
    customerSaleCurrency: showAmounts ? String(row.customerSaleCurrency || "").trim().toUpperCase() || null : null,
    agentCommissionType: showAmounts ? commissionType : null,
    agentCommissionValue: showAmounts ? finiteNumber(row.agentCommissionValue) : null,
    offerCode: row.offerCode || null,
    searchId: row.searchId || null,
    offerToken: row.offerToken || null,
    expiresAt: row.expiresAt || null,
    roomCount: row.roomCount || null,
  };
}

export function hubwayzQuoteBlocksPayment(quote) {
  return !quote || quote.source !== "server_quote" || quote.binding !== true;
}

/**
 * Hotel-details summary for a server quote.
 * A missing quote does not fall back to a supplier amount or a browser FX figure.
 * @returns {{ amount: number|null, currency: "ILS", verified: boolean } | null}
 */
export function hubwayzVerifiedSummaryMoney(quote) {
  if (!quote || quote.source !== "server_quote") return null;
  const amount = finiteNumber(quote.agentNetIls);
  if (amount == null || amount <= 0) {
    return { amount: null, currency: "ILS", verified: false };
  }
  return { amount, currency: "ILS", verified: true };
}

function shownServerAmount(row) {
  const deal = finiteNumber(row?.dealAmount);
  const dealCurrency = String(row?.dealCurrency || "").trim().toUpperCase();
  if (deal != null && deal > 0 && dealCurrency) {
    return { amount: deal, currency: dealCurrency };
  }
  return {
    amount: finiteNumber(row?.agentPriceAmount),
    currency: String(row?.agentPriceCurrency || "").trim().toUpperCase(),
  };
}

export function verifiedHubwayzListPrice(quotes) {
  const rows = (Array.isArray(quotes) ? quotes : []).filter((row) => {
    const shown = shownServerAmount(row);
    return row && row.binding === true && shown.amount > 0 && shown.currency;
  });
  rows.sort((a, b) => shownServerAmount(a).amount - shownServerAmount(b).amount);
  return rows[0] || null;
}

/**
 * Search and hotel-card money. HotelsRiver margin is already inside the amount.
 * @returns {{ amount: number|null, currency: string|null, verified: boolean } | null}
 */
export function hubwayzVerifiedOfferMoney(quote) {
  if (!quote || quote.source !== "server_quote") return null;
  const shown = shownServerAmount(quote);
  if (shown.amount == null || shown.amount <= 0 || !shown.currency) {
    return { amount: null, currency: shown.currency || null, verified: false };
  }
  return { amount: shown.amount, currency: shown.currency, verified: true };
}

export function hubwayzVerifiedPriceTrio(quote) {
  if (!quote || quote.source !== "server_quote" || !finiteNumber(quote.agentNetIls)) {
    return { list: null, card: null, summary: null };
  }
  const shown = shownServerAmount(quote);
  const offerAmount = shown.amount;
  return {
    list: offerAmount,
    card: offerAmount,
    summary: {
      agentPriceAmount: offerAmount,
      agentPriceCurrency: shown.currency || quote.agentPriceCurrency || null,
      agentNetIls: Number(quote.agentNetIls),
      agentMarkupIls: Number(quote.agentMarkupIls) || 0,
      customerSaleIls: finiteNumber(quote.customerSaleIls),
      chargeIls: finiteNumber(quote.chargeIls),
    },
  };
}
